import { useQuery } from '@tanstack/react-query';
import { UzButton, UzConfirmDialog, UzInput, UzSelect } from '@utazzo/ui';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useCan } from '../lib';

import { AddMenuItemsPanel, draftsFromPicker, type MenuItemDraftInput } from './AddMenuItemsPanel';
import {
  insertFooterLinks,
  lastFooterSectionId,
  createFooterSection,
  flatToFooterSections,
} from './footer-menu.utils';
import { FooterMenuStructurePanel } from './FooterMenuStructurePanel';
import { MenuStructurePanel } from './MenuStructurePanel';
import { saveMenuDraft } from './navigation-save';
import { treeToFlat, type FlatDraftItem } from './navigation-tree.utils';
import { deleteMenu, getNavTree } from './navigation.api';
import { FOOTER_MENU_LOCATION, MENU_LOCATIONS } from './navigation.constants';

import { PageShell } from '@/components/page-shell';

export const NavigationPage = (): React.JSX.Element => {
  const canWrite = useCan('navigation.write');
  const canDeleteMenu = useCan('navigation.write');

  const [location, setLocation] = useState<string>(MENU_LOCATIONS[0]!.id);
  const [menuTitle, setMenuTitle] = useState('');
  const [draftItems, setDraftItems] = useState<FlatDraftItem[]>([]);
  const [serverItemIds, setServerItemIds] = useState<Set<string>>(new Set());
  const [menuId, setMenuId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const query = useQuery({
    queryKey: ['nav-tree', location],
    queryFn: () => getNavTree(location),
  });

  const handleLocationChange = (next: string): void => {
    if (next === location) return;
    if (dirty && !window.confirm('You have unsaved menu changes. Switch menu anyway?')) {
      return;
    }
    setDirty(false);
    setLocation(next);
  };

  const locationMeta = MENU_LOCATIONS.find((l) => l.id === location);
  const isFooter = location === FOOTER_MENU_LOCATION;
  const [footerSectionId, setFooterSectionId] = useState<string | null>(null);

  const loadFromServer = useCallback(
    (data: Awaited<ReturnType<typeof getNavTree>>) => {
      const flat = treeToFlat(data.items);
      setDraftItems(flat);
      setServerItemIds(new Set(flat.map((i) => i.id)));
      setMenuId(data.menuId);
      setMenuTitle(data.title ?? locationMeta?.label ?? location);
      setDirty(false);
    },
    [location, locationMeta?.label],
  );

  useEffect(() => {
    if (query.data) loadFromServer(query.data);
  }, [query.data, loadFromServer]);

  useEffect(() => {
    if (!isFooter) return;
    setFooterSectionId((current) => current ?? lastFooterSectionId(draftItems));
  }, [isFooter, draftItems]);

  const markDirty = (): void => setDirty(true);

  const handleAddItems = (inputs: MenuItemDraftInput[]): void => {
    if (!canWrite || inputs.length === 0) return;
    const picked = draftsFromPicker(inputs, isFooter ? 1 : 0);

    if (isFooter) {
      let targetId = footerSectionId ?? lastFooterSectionId(draftItems);
      let next = draftItems;
      if (!targetId) {
        const section = createFooterSection();
        targetId = section.id;
        next = [...draftItems, section];
      }
      setDraftItems(insertFooterLinks(next, targetId, picked));
      setFooterSectionId(targetId);
    } else {
      setDraftItems((prev) => [...prev, ...picked]);
    }

    markDirty();
    toast.success(
      inputs.length === 1 ? '1 item added to menu' : `${inputs.length} items added to menu`,
    );
  };

  const handleSave = async (): Promise<void> => {
    if (!canWrite) return;
    setSaving(true);
    try {
      const id = await saveMenuDraft({
        location,
        menuId,
        menuTitle: menuTitle.trim() || locationMeta?.label || 'Menu',
        items: draftItems,
        serverItemIds,
      });
      setMenuId(id);
      setDirty(false);
      toast.success('Menu saved');
      const fresh = await query.refetch();
      if (fresh.data) loadFromServer(fresh.data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save menu');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMenu = async (): Promise<void> => {
    if (!menuId || !canDeleteMenu) return;
    try {
      await deleteMenu(menuId);
      toast.success('Menu deleted');
      setDeleteOpen(false);
      setMenuId(null);
      setDraftItems([]);
      setServerItemIds(new Set());
      setDirty(false);
      await query.refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete menu');
    }
  };

  const activeCount = useMemo(() => draftItems.filter((i) => !i.isDeleted).length, [draftItems]);

  const footerSectionOptions = useMemo(() => {
    if (!isFooter) return [];
    return flatToFooterSections(draftItems).map((g) => ({
      id: g.section.id,
      label: g.section.label,
    }));
  }, [draftItems, isFooter]);

  const menuLocationOptions = useMemo(
    () =>
      MENU_LOCATIONS.map((loc) => ({
        value: loc.id,
        label: loc.label,
      })),
    [],
  );

  return (
    <PageShell
      title="Menus"
      description="Edit site navigation - add pages, posts, and custom links, then drag to order and nest."
    >
      <div className="rounded-lg border border-(--border) bg-(--z-50) p-4">
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
          <UzSelect
            label="Select a menu to edit"
            hint={locationMeta?.desc}
            value={location}
            onValueChange={handleLocationChange}
            options={menuLocationOptions}
            triggerClassName="w-full bg-(--bg)"
          />
          <UzInput
            label="Menu name"
            hint="Internal label for this menu in the admin."
            value={menuTitle}
            disabled={!canWrite}
            onChange={(e) => {
              setMenuTitle(e.target.value);
              markDirty();
            }}
            placeholder="e.g. Footer"
            containerClassName="w-full"
          />
          {dirty && (
            <div className="flex h-9 items-center lg:pt-6">
              <span className="inline-flex items-center rounded-full border border-(--primary)/20 bg-(--accent) px-3 py-1 text-xs font-medium text-(--primary)">
                Unsaved changes
              </span>
            </div>
          )}
        </div>
      </div>

      {query.isLoading ? (
        <p className="py-16 text-center text-sm text-(--fg-faint)">Loading menu…</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(280px,340px)_1fr]">
          <div className="rounded-lg border border-(--border) bg-(--bg) p-4">
            {isFooter && footerSectionOptions.length > 0 && (
              <UzSelect
                label="Add links to section"
                hint="New links from the left panel are placed in this section."
                value={footerSectionId ?? footerSectionOptions[0]?.id ?? ''}
                onValueChange={setFooterSectionId}
                options={footerSectionOptions.map((s) => ({ value: s.id, label: s.label }))}
                triggerClassName="w-full bg-(--bg)"
                containerClassName="mb-3"
              />
            )}
            <AddMenuItemsPanel canWrite={canWrite} onAdd={handleAddItems} footerMode={isFooter} />
          </div>

          <div className="rounded-lg border border-(--border) bg-(--bg) p-4">
            {isFooter ? (
              <FooterMenuStructurePanel
                items={draftItems}
                canWrite={canWrite}
                onChange={(next) => {
                  setDraftItems(next);
                  markDirty();
                }}
              />
            ) : (
              <MenuStructurePanel
                items={draftItems}
                canWrite={canWrite}
                onChange={(next) => {
                  setDraftItems(next);
                  markDirty();
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* Footer actions - WordPress Save / Delete */}
      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-(--border) bg-(--z-50) px-4 py-3">
        {canWrite && (
          <UzButton loading={saving} onClick={() => void handleSave()}>
            Save Menu
          </UzButton>
        )}
        {canDeleteMenu && menuId && (
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            className="text-sm text-(--ds-error) hover:underline"
          >
            Delete Menu
          </button>
        )}
        <span className="text-xs text-(--fg-faint)">
          {activeCount} item{activeCount === 1 ? '' : 's'} in this menu
        </span>
      </div>

      <UzConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this menu?"
        description={`"${menuTitle}" and all ${activeCount} items will be removed. The website will show an empty menu for this location until you create a new one.`}
        confirmLabel="Delete Menu"
        tone="destructive"
        onConfirm={() => void handleDeleteMenu()}
      />
    </PageShell>
  );
};
